import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';

interface Props {
  open: boolean;
  onClose: () => void;
  count: number;
  onConfirm: (reason: string) => Promise<void>;
  mode?: 'redact' | 'revive' | 'deactivate' | 'reactivate';
}

const BulkRedactConfirmModal: React.FC<Props> = ({
  open,
  onClose,
  count,
  onConfirm,
  mode = 'redact',
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isRevive = mode === 'revive';
  const isDeactivate = mode === 'deactivate';
  const isReactivate = mode === 'reactivate';

  const handleClose = () => {
    if (submitting) return;
    setReason('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      // The caller (onConfirm) owns ALL outcome messaging — success, partial,
      // and failure — so the modal must not emit its own success toast, which
      // previously fired even when every participant failed.
      await onConfirm(reason.trim());
      setReason('');
      onClose();
    } catch {
      // errors are handled by the caller via toast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isRevive
          ? 'Confirm Bulk Revival'
          : isReactivate
          ? 'Confirm Bulk Reactivation'
          : isDeactivate
          ? 'Confirm Bulk Deactivation'
          : 'Confirm Bulk Redaction'}
      </DialogTitle>
      <DialogContent>
        <Alert
          severity={isRevive || isReactivate ? 'info' : 'warning'}
          sx={{ mb: 2 }}
        >
          {isRevive ? (
            <>
              You are about to revive <strong>{count}</strong> participant(s).
              Only PII fields will be restored — all non-PII data remains
              permanently removed.
            </>
          ) : isReactivate ? (
            <>
              You are about to reactivate <strong>{count}</strong>{' '}
              participant(s), returning them to active. No PII is affected — this
              simply reverses the deactivation.
            </>
          ) : isDeactivate ? (
            <>
              You are about to deactivate <strong>{count}</strong>{' '}
              participant(s). Deactivation is reversible and removes no PII. Each
              participant must already be redacted in every study they are
              enrolled in, or the action will be rejected.
            </>
          ) : (
            <>
              You are about to redact <strong>{count}</strong> participant(s).
              This action cannot be fully undone.
            </>
          )}
        </Alert>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Please provide a reason. This will be recorded in the audit trail for
          every affected participant.
        </Typography>
        <TextField
          label="Reason"
          multiline
          minRows={3}
          fullWidth
          required
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 1000))}
          inputProps={{ maxLength: 1000 }}
          helperText={`${reason.length}/1000`}
          disabled={submitting}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={
            isRevive || isReactivate
              ? 'primary'
              : isDeactivate
              ? 'warning'
              : 'error'
          }
          onClick={handleConfirm}
          disabled={!reason.trim() || submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {submitting
            ? isRevive
              ? 'Reviving…'
              : isReactivate
              ? 'Reactivating…'
              : isDeactivate
              ? 'Deactivating…'
              : 'Redacting…'
            : isRevive
            ? 'Confirm Revival'
            : isReactivate
            ? 'Confirm Reactivation'
            : isDeactivate
            ? 'Confirm Deactivation'
            : 'Confirm Redaction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkRedactConfirmModal;
